/**
 * @file Слой 3 - Намёк на глубину
 * @module membrane/src/layers/Layer3Hint
 * 
 * Третий слой мембраны - мягкий вызов внимательного.
 * Скрытые элементы, активирующиеся через внимание.
 * Разделение происходит естественно: кто не готов - остаётся на слоях 1-2.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { BaseLayerProps } from '../types/membrane';
import { useState, useEffect } from 'react';

/**
 * Компонент Layer3Hint - намёк на глубину
 * 
 * Показывает скрытые элементы, которые активируются через:
 * - время пребывания
 * - наведение курсора
 * - внимание (скролл, движение)
 * 
 * Не объясняет, а приглашает.
 */
export default function Layer3Hint({ onProceed, onReturn, state: _state }: BaseLayerProps) {
  const [hintVisible, setHintVisible] = useState(false);
  const [, setAttentionTime] = useState(0);

  // Активация через время внимания
  useEffect(() => {
    const interval = setInterval(() => {
      setAttentionTime((prev) => {
        const next = prev + 100;
        if (next >= 3000 && !hintVisible) {
          setHintVisible(true);
        }
        return next;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [hintVisible]);

  // Активация через движение мыши
  useEffect(() => {
    let mouseStillTime = 0;
    const handleMouseMove = () => {
      mouseStillTime = 0;
    };

    const handleMouseStill = () => {
      mouseStillTime += 100;
      if (mouseStillTime >= 2000 && !hintVisible) {
        setHintVisible(true);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    const stillInterval = setInterval(handleMouseStill, 100);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(stillInterval);
    };
  }, [hintVisible]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full"
    >
      {/* Фон */}
      <div className="absolute inset-0 bg-gradient-to-b from-membrane-light via-white to-membrane-light/50" />

      {/* Основной контент - почти пустой, создаёт ощущение пустоты */}
      <div className="absolute inset-0 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
          className="text-center max-w-xl"
        >
          <motion.p
            className="text-base md:text-lg font-light text-membrane-depth/50"
            animate={{
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            Если хочешь — смотри глубже
          </motion.p>
        </motion.div>
      </div>

      {/* Скрытый намёк - появляется для внимательного */}
      <AnimatePresence>
        {hintVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
            className="absolute bottom-24 left-1/2 transform -translate-x-1/2"
          >
            <motion.div
              animate={{
                y: [0, -10, 0],
                opacity: [0.4, 0.7, 0.4],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="flex flex-col items-center gap-2 cursor-pointer"
              onClick={() => onProceed?.()}
            >
              <div className="w-px h-16 bg-membrane-depth/30" />
              <div className="w-2 h-2 rounded-full bg-membrane-depth/40" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Возможность вернуться */}
      {onReturn && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          whileHover={{ opacity: 0.5 }}
          onClick={onReturn}
          className="absolute top-8 left-8 text-membrane-depth/40 hover:text-membrane-depth/60 transition-opacity"
          aria-label="Вернуться"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
      )}
    </motion.div>
  );
}

